import { Check } from 'lucide-react';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import type { BrowserAiToolsTrendCategory } from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TREND_CONFIG } from './trendConfig';

interface DomainFilterOptionProps {
  domain: string;
  isSelected: boolean;
}

export function DomainFilterOption({
  domain,
  isSelected,
}: DomainFilterOptionProps) {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <span className="text-xs font-mono text-muted-foreground">{domain}</span>
    </div>
  );
}

interface UserFilterOptionProps {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  isSelected: boolean;
}

export function BrowserAiToolsUserFilterOption({
  userId,
  userName,
  userEmail,
  isSelected,
}: UserFilterOptionProps) {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-sm font-medium truncate">
          {userName || userEmail || userId}
        </span>
        {userName && userEmail && (
          <span className="text-xs text-muted-foreground truncate">
            {userEmail}
          </span>
        )}
      </div>
    </div>
  );
}

interface CategoryFilterOptionProps {
  category: string;
  isSelected: boolean;
}

export function CategoryFilterOption({
  category,
  isSelected,
}: CategoryFilterOptionProps) {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <Badge variant="secondary" className="text-xs">
        {getShadowAiCategoryLabel(category)}
      </Badge>
    </div>
  );
}

interface TrendFilterOptionProps {
  trend: BrowserAiToolsTrendCategory | (string & {});
  isSelected: boolean;
}

export function TrendFilterOption({
  trend,
  isSelected,
}: TrendFilterOptionProps) {
  const config = TREND_CONFIG[trend as BrowserAiToolsTrendCategory] ?? {
    label: trend,
    className: '',
  };
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <span
        className={cn(
          'text-xs font-medium flex items-center gap-1',
          config.className
        )}
      >
        {config.icon}
        {config.label}
      </span>
    </div>
  );
}
